import { IBookingCalculationDto } from 'src/lib/repository/booking/booking-calculation.model';
import { BookingExchangeRate } from 'src/lib/repository/booking/booking.model';
import { CurrencyCd } from 'src/lib/repository/enums';
import { Utils } from 'src/lib/utils';
import {
  CurrencyCdAndAmt,
  getCostingTotalAmt,
  getDepositTotalAmt,
  getPrpayTotalAmt,
} from './calculation.utils';

/**
 * ewrc (인당) 데이터 변환
 *
 * IBookingCalculationDto[] → 멤버별 행으로 변환 + 인당 나눗셈
 */
export function transformEwrcData(calcList: IBookingCalculationDto[]): any[] {
  const result: any[] = [];

  calcList
    .filter((data) => data && !!data.bookingMember?.length)
    .forEach((data) => {
      const tourNumber = data.bookingMember.length;
      const exchangeRate: BookingExchangeRate = Utils.getParsedJson(
        data.exchangeRateJson,
        {},
      );
      const flightsSellingTotalAmt = getCostingTotalAmt(
        data.bookingCostingList,
        'FLIGHTS',
        'sellingTotalAmt',
        data.currencyCd,
        exchangeRate,
      );
      const bookingManagementTotalFee = getCostingTotalAmt(
        data.bookingCostingList,
        'BOOKING_MANAGEMENT',
        'sellingTotalAmt',
        data.currencyCd,
        exchangeRate,
      );
      const golfCostTotalAmt = getCostingTotalAmt(
        data.bookingCostingList,
        'GOLF',
        'costTotalAmt',
        data.currencyCd,
        exchangeRate,
      );
      const hotelCostTotalAmt = getCostingTotalAmt(
        data.bookingCostingList,
        'HOTEL',
        'costTotalAmt',
        data.currencyCd,
        exchangeRate,
      );
      const carCostTotalAmt = getCostingTotalAmt(
        data.bookingCostingList,
        'CAR',
        'costTotalAmt',
        data.currencyCd,
        exchangeRate,
      );
      const hotelSingleCostTotalAmt = getCostingTotalAmt(
        data.bookingCostingList,
        'HOTEL_SINGLE',
        'costTotalAmt',
        data.currencyCd,
        exchangeRate,
      );
      const golfAddRoundCostTotalAmt = getCostingTotalAmt(
        data.bookingCostingList,
        'GOLF_ADD_ROUND',
        'costTotalAmt',
        data.currencyCd,
        exchangeRate,
      );
      const etcCostTotalAmt = getCostingTotalAmt(
        data.bookingCostingList,
        'ETC',
        'costTotalAmt',
        data.currencyCd,
        exchangeRate,
      );
      const flightsCostTotalAmt = getCostingTotalAmt(
        data.bookingCostingList,
        'FLIGHTS',
        'costTotalAmt',
        data.currencyCd,
        exchangeRate,
      );
      const prpayTotalAmt = getPrpayTotalAmt(data.paymentList);

      const hotelGoodsNm =
        data.bookingCostingList?.find(
          ({ bookingCostingType }) => bookingCostingType === 'HOTEL',
        )?.itemNm || '';

      const flightsSellingAmt: CurrencyCdAndAmt = [
        flightsSellingTotalAmt[0],
        flightsSellingTotalAmt[1] / tourNumber,
      ];
      const bookingManagementFee: CurrencyCdAndAmt = [
        bookingManagementTotalFee[0],
        bookingManagementTotalFee[1] / tourNumber,
      ];
      const systemFee: CurrencyCdAndAmt = [
        data.currencyCd,
        (data.sellingTotalAmt / tourNumber) * (data.systemFee / 100),
      ];
      const sellingTotalAmt: CurrencyCdAndAmt = [
        data.currencyCd,
        data.sellingTotalAmt / tourNumber,
      ];
      const prpayAmt: CurrencyCdAndAmt = [
        data.currencyCd,
        prpayTotalAmt / tourNumber,
      ];
      const bookingDeposit: CurrencyCdAndAmt = [
        data.currencyCd,
        data.bookingDeposit,
      ];
      const paidTotalAmt: CurrencyCdAndAmt = [
        data.currencyCd,
        (data.paidTotalAmt - prpayTotalAmt - data.depositRefundedTotalAmt) /
          tourNumber,
      ];
      const golfCostAmt: CurrencyCdAndAmt = [
        golfCostTotalAmt[0],
        golfCostTotalAmt[1] / tourNumber,
      ];
      const hotelCostAmt: CurrencyCdAndAmt = [
        hotelCostTotalAmt[0],
        hotelCostTotalAmt[1] / tourNumber,
      ];
      const carCostAmt: CurrencyCdAndAmt = [
        carCostTotalAmt[0],
        carCostTotalAmt[1] / tourNumber,
      ];
      const hotelSingleCostAmt: CurrencyCdAndAmt = [
        hotelSingleCostTotalAmt[0],
        hotelSingleCostTotalAmt[1] / tourNumber,
      ];
      const golfAddRoundCostAmt: CurrencyCdAndAmt = [
        golfAddRoundCostTotalAmt[0],
        golfAddRoundCostTotalAmt[1] / tourNumber,
      ];
      const etcCostAmt: CurrencyCdAndAmt = [
        etcCostTotalAmt[0],
        etcCostTotalAmt[1] / tourNumber,
      ];
      const flightsCostAmt: CurrencyCdAndAmt = [
        flightsCostTotalAmt[0],
        flightsCostTotalAmt[1] / tourNumber,
      ];

      const laborDirectCostAmt: CurrencyCdAndAmt = ['JPY', 0];
      const carMaintainDirectCostAmt: CurrencyCdAndAmt = ['JPY', 0];
      const etcDirectCostAmt: CurrencyCdAndAmt = ['JPY', 0];
      const indirectCostAmt: CurrencyCdAndAmt = ['JPY', 0];

      const flightsSellingExchangedAmt = Utils.getExchangedAmt(
        flightsSellingAmt[1],
        exchangeRate,
        data.currencyCd,
        flightsSellingAmt[0] as CurrencyCd,
        data.currencyCd,
      );

      const groundAmt: CurrencyCdAndAmt = [
        data.currencyCd,
        data.sellingTotalAmt / 4 - flightsSellingExchangedAmt,
      ];
      const toPayAmt: CurrencyCdAndAmt = [
        data.currencyCd,
        sellingTotalAmt[1] - prpayAmt[1] - bookingDeposit[1],
      ];
      const notPaidAmt: CurrencyCdAndAmt = [
        data.currencyCd,
        toPayAmt[1] - paidTotalAmt[1],
      ];

      data.bookingMember?.forEach((member) => {
        result.push({
          id: data.id,
          agentName: data.agentName || data.tradeCode,
          startPoint: data.startPoint,
          destinationPoint: data.destinationPoint,
          tourRegionCode: data.tourRegionCode,
          userNm: member.userNm,
          userClass: member.userClass,
          hotelGoodsNm,
          startDate: data.startDate,
          returnDate: data.returnDate,
          groundAmt,
          airplaneName: data.airplaneName,
          flightsSellingAmt,
          bookingManagementFee,
          systemFee,
          sellingTotalAmt,
          prpayAmt,
          bookingDeposit,
          toPayAmt,
          paidTotalAmt,
          notPaidAmt,
          golfCostAmt,
          hotelCostAmt,
          carCostAmt,
          hotelSingleCostAmt,
          golfAddRoundCostAmt,
          etcCostAmt,
          flightsCostAmt,
          laborDirectCostAmt,
          carMaintainDirectCostAmt,
          etcDirectCostAmt,
          indirectCostAmt,
        });
      });
    });

  return result;
}

/**
 * booking (예약별) 데이터 변환
 *
 * IBookingCalculationDto[] → 예약별 행으로 변환 (인당 나눗셈 없음)
 */
export function transformBookingData(
  calcList: IBookingCalculationDto[],
): any[] {
  const result: any[] = [];

  calcList.forEach((data) => {
    const { tourNumber } = data;
    const exchangeRate: BookingExchangeRate = Utils.getParsedJson(
      data.exchangeRateJson,
      {},
    );
    const flightsSellingTotalAmt = getCostingTotalAmt(
      data.bookingCostingList,
      'FLIGHTS',
      'sellingTotalAmt',
      data.currencyCd,
      exchangeRate,
    );
    const bookingManagementTotalFee = getCostingTotalAmt(
      data.bookingCostingList,
      'BOOKING_MANAGEMENT',
      'sellingTotalAmt',
      data.currencyCd,
      exchangeRate,
    );
    const golfCostTotalAmt = getCostingTotalAmt(
      data.bookingCostingList,
      'GOLF',
      'costTotalAmt',
      data.currencyCd,
      exchangeRate,
    );
    const hotelCostTotalAmt = getCostingTotalAmt(
      data.bookingCostingList,
      'HOTEL',
      'costTotalAmt',
      data.currencyCd,
      exchangeRate,
    );
    const carCostTotalAmt = getCostingTotalAmt(
      data.bookingCostingList,
      'CAR',
      'costTotalAmt',
      data.currencyCd,
      exchangeRate,
    );
    const hotelSingleCostTotalAmt = getCostingTotalAmt(
      data.bookingCostingList,
      'HOTEL_SINGLE',
      'costTotalAmt',
      data.currencyCd,
      exchangeRate,
    );
    const golfAddRoundCostTotalAmt = getCostingTotalAmt(
      data.bookingCostingList,
      'GOLF_ADD_ROUND',
      'costTotalAmt',
      data.currencyCd,
      exchangeRate,
    );
    const etcCostTotalAmt = getCostingTotalAmt(
      data.bookingCostingList,
      'ETC',
      'costTotalAmt',
      data.currencyCd,
      exchangeRate,
    );
    const flightsCostTotalAmt = getCostingTotalAmt(
      data.bookingCostingList,
      'FLIGHTS',
      'costTotalAmt',
      data.currencyCd,
      exchangeRate,
    );
    const prpayTotalAmt = getPrpayTotalAmt(data.paymentList);

    const hotelGoodsNm =
      data.bookingCostingList?.find(
        ({ bookingCostingType }) => bookingCostingType === 'HOTEL',
      )?.itemNm || '';

    const flightsSellingAmt: CurrencyCdAndAmt = [
      flightsSellingTotalAmt[0],
      flightsSellingTotalAmt[1],
    ];
    const bookingManagementFee: CurrencyCdAndAmt = [
      bookingManagementTotalFee[0],
      bookingManagementTotalFee[1],
    ];
    const systemFee: CurrencyCdAndAmt = [
      data.currencyCd,
      data.sellingTotalAmt * (data.systemFee / 100),
    ];
    const prpayAmt: CurrencyCdAndAmt = [data.currencyCd, prpayTotalAmt];
    const bookingDeposit: CurrencyCdAndAmt = [
      data.currencyCd,
      data.bookingDeposit,
    ];
    const paidTotalAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      data.paidTotalAmt - prpayTotalAmt - data.depositRefundedTotalAmt,
    ];
    const golfCostAmt: CurrencyCdAndAmt = [
      golfCostTotalAmt[0],
      golfCostTotalAmt[1],
    ];
    const hotelCostAmt: CurrencyCdAndAmt = [
      hotelCostTotalAmt[0],
      hotelCostTotalAmt[1],
    ];
    const carCostAmt: CurrencyCdAndAmt = [
      carCostTotalAmt[0],
      carCostTotalAmt[1],
    ];
    const hotelSingleCostAmt: CurrencyCdAndAmt = [
      hotelSingleCostTotalAmt[0],
      hotelSingleCostTotalAmt[1],
    ];
    const golfAddRoundCostAmt: CurrencyCdAndAmt = [
      golfAddRoundCostTotalAmt[0],
      golfAddRoundCostTotalAmt[1],
    ];
    const etcCostAmt: CurrencyCdAndAmt = [
      etcCostTotalAmt[0],
      etcCostTotalAmt[1],
    ];
    const flightsCostAmt: CurrencyCdAndAmt = [
      flightsCostTotalAmt[0],
      flightsCostTotalAmt[1],
    ];

    const laborDirectCostAmt: CurrencyCdAndAmt = ['JPY', 0];
    const carMaintainDirectCostAmt: CurrencyCdAndAmt = ['JPY', 0];
    const etcDirectCostAmt: CurrencyCdAndAmt = ['JPY', 0];
    const indirectCostAmt: CurrencyCdAndAmt = ['JPY', 0];

    const localSellingTotalAmt: CurrencyCdAndAmt = [
      'JPY',
      flightsSellingAmt[1] +
        hotelCostAmt[1] +
        carCostAmt[1] +
        golfCostAmt[1] +
        hotelSingleCostAmt[1] +
        golfAddRoundCostAmt[1] +
        etcCostAmt[1],
    ];

    const rate = exchangeRate['JPY'] || 1;

    const packagegoodsSellingFee: CurrencyCdAndAmt = [
      data.packagegoodsSellingFeeCurrencyCd,
      data.packagegoodsBookingManagementFeeIncludedFlg
        ? data.packagegoodsSellingFee
        : 0,
    ];
    const packagegoodsSellingFeeExchangedAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      Math.round(packagegoodsSellingFee[1] * rate),
    ];
    const exchangedCostAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      Math.round(localSellingTotalAmt[1] * rate),
    ];

    const flightsSellingExchangedAmt = Utils.getExchangedAmt(
      flightsSellingAmt[1],
      exchangeRate,
      data.currencyCd,
      flightsSellingAmt[0] as CurrencyCd,
      data.currencyCd,
    );

    const sellingTotalAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      exchangedCostAmt[1] + bookingManagementTotalFee[1],
    ];
    const groundAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      data.sellingTotalAmt / 4 - flightsSellingExchangedAmt,
    ];
    const toPayAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      sellingTotalAmt[1] - prpayAmt[1] - bookingDeposit[1],
    ];
    const notPaidAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      toPayAmt[1] - paidTotalAmt[1],
    ];

    const depositBookingManagementFee: CurrencyCdAndAmt = [
      data.currencyCd,
      getDepositTotalAmt(data.paymentList, 'depositBookingManagementFee'),
    ];
    const depositFlightsFee: CurrencyCdAndAmt = [
      data.currencyCd,
      getDepositTotalAmt(data.paymentList, 'depositFlightsFee'),
    ];
    const depositRefundPrice: CurrencyCdAndAmt = [
      data.currencyCd,
      getDepositTotalAmt(data.paymentList, 'depositRefundPrice'),
    ];
    const depositRemainAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      getDepositTotalAmt(data.paymentList, 'amt') -
        depositBookingManagementFee[1] -
        depositFlightsFee[1] -
        depositRefundPrice[1],
    ];
    const depositTotalAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      depositBookingManagementFee[1] +
        depositFlightsFee[1] +
        depositRemainAmt[1],
    ];
    const depositNotPaidAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      Math.max(0, sellingTotalAmt[1] - depositTotalAmt[1]),
    ];

    const tourTerm = +(data.tourTerm?.split('N')[0] ?? 0);

    const exchangedSystemFee: CurrencyCdAndAmt = [
      data.currencyCd,
      Utils.getExchangedAmt(
        systemFee[1] * tourTerm,
        exchangeRate,
        data.currencyCd,
        systemFee[0] as CurrencyCd,
        data.currencyCd,
      ),
    ];

    const resortTransferNotPaidAmt: CurrencyCdAndAmt = [
      data.currencyCd,
      Math.round(
        Math.max(
          0,
          exchangedCostAmt[1] -
            depositFlightsFee[1] -
            (depositBookingManagementFee[1] +
              depositRemainAmt[1] +
              bookingManagementFee[1] +
              packagegoodsSellingFeeExchangedAmt[1] * tourTerm * rate),
        ),
      ),
    ];

    result.push({
      tourNumber,
      id: data.id,
      agentName: data.agentName || data.tradeCode,
      packagegoodsTitle: data.packagegoodsTitle,
      bookingTitle: data.bookingTitle,
      startPoint: data.startPoint,
      destinationPoint: data.destinationPoint,
      tourRegionCode: data.tourRegionCode,
      hotelGoodsNm,
      startDate: data.startDate,
      returnDate: data.returnDate,
      tourTerm,
      groundAmt,
      airplaneName: data.airplaneName,
      flightsSellingAmt,
      bookingManagementFee,
      systemFee,
      sellingTotalAmt,
      prpayAmt,
      bookingDeposit,
      toPayAmt,
      paidTotalAmt,
      notPaidAmt,
      golfCostAmt,
      hotelCostAmt,
      carCostAmt,
      hotelSingleCostAmt,
      golfAddRoundCostAmt,
      etcCostAmt,
      flightsCostAmt,
      laborDirectCostAmt,
      carMaintainDirectCostAmt,
      etcDirectCostAmt,
      indirectCostAmt,
      packagegoodsSellingFee,
      packagegoodsSellingFeeExchangedAmt,
      localSellingTotalAmt,
      exchangeRate: rate,
      exchangeRateDttm: data.exchangeRateDttm,
      exchangedCostAmt,
      depositBookingManagementFee,
      depositFlightsFee,
      depositRefundPrice,
      depositRemainAmt,
      depositTotalAmt,
      depositNotPaidAmt,
      exchangedSystemFee,
      resortTransferNotPaidAmt,
    });
  });

  return result;
}
